import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { Loader2, Heart, AlertCircle, CheckCircle, ArrowLeft, Lock, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const passwordRequirements = [
  { label: 'At least 6 characters', test: (p: string) => p.length >= 6 },
  { label: 'Contains a number', test: (p: string) => /\d/.test(p) },
];

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check existing session (for page reload)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setIsValidSession(true);
    });

    // Also listen for PASSWORD_RECOVERY event — Supabase fires this when the
    // user arrives from the reset-password email link with a token in the URL hash
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') && session) {
        setIsValidSession(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) {
        setError(error.message);
      } else {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div className="absolute top-0 left-1/3 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float-slow" />
      <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-float-delayed" />
      
      <motion.div 
        className="w-full max-w-md relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Back to Login */}
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 group"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          Back to login
        </Link>

        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <motion.div 
            className="relative"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Heart className="h-10 w-10 text-primary" />
            <div className="absolute inset-0 bg-primary/30 blur-xl rounded-full" />
          </motion.div>
          <span className="text-2xl font-bold text-foreground">
            Apne<span className="text-primary">Doctors</span>
          </span>
        </div>

        <Card className="border-border/30 bg-card/80 backdrop-blur-xl shadow-2xl overflow-hidden">
          {/* Gradient Top Border */}
          <div className="h-1 bg-gradient-to-r from-primary via-secondary to-primary" />
          
          <CardHeader className="space-y-4 pt-8 text-center">
            {/* Icon */}
            <div className="mx-auto w-fit p-4 rounded-2xl bg-gradient-to-br from-card to-card/50 border border-border/50 shadow-lg">
              <Lock className="h-10 w-10 text-primary" />
            </div>
            
            <CardTitle className="text-2xl">Set new password</CardTitle>
            <CardDescription className="text-muted-foreground">
              Create a new password for your account.
            </CardDescription>
          </CardHeader>

          {success ? (
            <CardContent className="space-y-4 px-6 pb-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Alert className="border-green-500/30 bg-green-500/10">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <AlertDescription className="text-green-500">
                    Password updated successfully! Redirecting to login...
                  </AlertDescription>
                </Alert>
              </motion.div>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4 px-6">
                {/* Error Alert */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  </motion.div>
                )}
                
                {/* New Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    New Password
                  </Label>
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="bg-background/50 border-border/50 focus:border-primary focus:ring-primary/20"
                  />
                  {/* Password Requirements */}
                  <div className="space-y-1 pt-1">
                    {passwordRequirements.map((req, i) => (
                      <div 
                        key={i}
                        className={cn(
                          'flex items-center gap-2 text-xs transition-colors',
                          req.test(password) ? 'text-green-500' : 'text-muted-foreground'
                        )}
                      >
                        <Check className={cn(
                          'h-3 w-3 transition-opacity',
                          req.test(password) ? 'opacity-100' : 'opacity-30'
                        )} />
                        {req.label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">
                    Confirm Password
                  </Label>
                  <PasswordInput
                    id="confirmPassword"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="bg-background/50 border-border/50 focus:border-primary focus:ring-primary/20"
                  />
                  {confirmPassword && (
                    <div className={cn(
                      'flex items-center gap-2 text-xs transition-colors',
                      password === confirmPassword ? 'text-green-500' : 'text-destructive'
                    )}>
                      <Check className={cn(
                        'h-3 w-3',
                        password === confirmPassword ? 'opacity-100' : 'opacity-30'
                      )} />
                      {password === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                    </div>
                  )}
                </div>
              </CardContent>
              
              <CardFooter className="flex flex-col gap-4 px-6 pb-8">
                <Button 
                  type="submit" 
                  className="w-full h-12 text-base font-semibold relative overflow-hidden group"
                  disabled={isLoading || !isValidSession}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-primary/0 via-white/20 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Updating password...
                    </>
                  ) : (
                    <>
                      <Lock className="mr-2 h-5 w-5" />
                      Update Password
                    </>
                  )}
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>

        {/* Footer */}
        <p className="text-xs text-muted-foreground text-center mt-6">
          Need help? Contact{' '}
          <a href="mailto:support@apnedoctors.com" className="text-primary hover:underline">
            support@apnedoctors.com
          </a>
        </p>
      </motion.div>
    </div>
  );
}
